import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBackend } from '../hooks/useBackend';

const WorkflowsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { apiCall } = useBackend();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Load workflows
  useEffect(() => {
    const loadWorkflows = async () => {
      try {
        setLoading(true);
        const data = await apiCall('/workflows');
        setWorkflows(data || []);
      } catch (err) {
        setError('Failed to load workflows');
        console.error('Error loading workflows:', err);
      } finally {
        setLoading(false);
      }
    };

    loadWorkflows();
  }, [apiCall]);

  const handleExecuteWorkflow = async (workflowId) => {
    try {
      setError('');
      await apiCall(`/workflows/${workflowId}/execute`, 'POST');
      // Refresh workflows to update status
      const data = await apiCall('/workflows');
      setWorkflows(data || []);
    } catch (err) {
      setError('Failed to execute workflow');
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) return;
    
    try {
      setError('');
      await apiCall(`/workflows/${workflowId}`, 'DELETE');
      setWorkflows(workflows.filter(w => w.id !== workflowId));
    } catch (err) {
      setError('Failed to delete workflow');
    }
  };

  const filteredWorkflows = workflows.filter(workflow => {
    const matchesSearch = workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         workflow.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || workflow.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'PAUSED': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'FAILED': return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
      case 'COMPLETED': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400';
      default: return 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400';
    }
  };

  return (
    <div style={{ backgroundColor: '#0a1116', color: 'white', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-[#233c48] backdrop-blur-md px-4 md:px-10 py-3" style={{ backgroundColor: '#0a1116' }}>
        <div className="max-w-[1440px] mx-auto flex items-center justify-between whitespace-nowrap">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 text-[#13a4ec]">
                <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="20" stroke="currentColor" strokeDasharray="10 4" strokeWidth="4"></circle>
                  <circle cx="24" cy="24" fill="currentColor" r="8"></circle>
                  <path d="M40 24C40 32.8366 32.8366 40 24 40" stroke="currentColor" strokeLinecap="round" strokeWidth="2"></path>
                </svg>
              </div>
              <h2 className="text-xl font-extrabold leading-tight tracking-tighter hidden sm:block">OrbitMind</h2>
            </div>
            <div className="hidden lg:flex items-center gap-6">
              <button onClick={() => navigate('/services')} className="text-sm font-medium text-slate-400 hover:text-[#13a4ec] transition-colors">Services Hub</button>
              <span className="text-sm font-semibold text-[#13a4ec] border-b-2 border-[#13a4ec] pb-1">Workflows</span>
              <button onClick={() => navigate('/infrastructure')} className="text-sm font-medium text-slate-400 hover:text-[#13a4ec] transition-colors">Infrastructure</button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="hidden md:flex flex-col min-w-40 h-10 max-w-64">
              <div className="flex w-full flex-1 items-stretch rounded-lg h-full border border-[#233c48] bg-[#1a2b34]">
                <div className="flex items-center justify-center pl-4">
                  <span className="material-symbols-outlined text-slate-400 text-[20px]">search</span>
                </div>
                <input 
                  className="w-full border-none bg-transparent focus:ring-0 text-sm placeholder:text-slate-500 pl-2 text-white" 
                  placeholder="Search workflows..." 
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </label>
            <div className="flex gap-2">
              <button className="flex items-center justify-center rounded-lg h-10 w-10 bg-[#233c48] hover:bg-[#13a4ec]/20 transition-colors">
                <span className="material-symbols-outlined text-[20px]">notifications</span>
              </button>
              <div className="h-10 w-10 rounded-full border-2 border-[#13a4ec]/20 p-0.5 overflow-hidden">
                <div className="w-full h-full object-cover rounded-full bg-slate-800 flex items-center justify-center">
                  <span className="material-symbols-outlined text-slate-400">account_circle</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto p-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-500">
              Workflow Management
            </h1>
            <p className="text-slate-500 mt-2 text-sm font-medium">
              Design, deploy, and monitor your automated workflows
            </p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => navigate('/workflow-builder')}
              className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 transition-all"
            >
              <span className="material-symbols-outlined text-[18px]">edit</span>
              Workflow Builder
            </button>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="bg-[#13a4ec] hover:bg-[#13a4ec]/90 text-white px-5 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 shadow-lg shadow-[#13a4ec]/30 transition-all active:scale-95"
            >
              <span className="material-symbols-outlined text-[18px]">add_circle</span>
              Create Workflow
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-red-400">error</span>
              <p className="text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-400">Status:</span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-[#233c48] border border-[#233c48] text-white rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
        </div>

        {/* Workflows Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-[#13a4ec]/30 border-t-[#13a4ec] rounded-full animate-spin"></div>
              <span className="text-slate-400">Loading workflows...</span>
            </div>
          </div>
        ) : filteredWorkflows.length === 0 ? (
          <div className="text-center py-12">
            <span className="material-symbols-outlined text-slate-500 text-6xl mb-4 block">workflow</span>
            <h3 className="text-slate-300 font-bold text-lg mb-2">No workflows found</h3>
            <p className="text-slate-500 text-sm mb-6">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your filters or search terms.' 
                : 'Create your first workflow to get started with automation.'
              }
            </p>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="bg-[#13a4ec] hover:bg-[#13a4ec]/90 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Create Your First Workflow
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWorkflows.map((workflow) => (
              <div 
                key={workflow.id} 
                className="group bg-[#16252d] border border-[#233c48] rounded-xl overflow-hidden hover:border-[#13a4ec]/50 hover:shadow-2xl hover:shadow-[#13a4ec]/5 transition-all duration-300"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-white mb-1">{workflow.name}</h3>
                      <p className="text-slate-400 text-sm line-clamp-2">{workflow.description}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(workflow.status)}`}>
                      {workflow.status}
                    </span>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="material-symbols-outlined text-slate-500 text-sm">schedule</span>
                      <span className="text-slate-400">
                        Last run: {workflow.lastExecuted ? new Date(workflow.lastExecuted).toLocaleDateString() : 'Never'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="material-symbols-outlined text-slate-500 text-sm">play_circle</span>
                      <span className="text-slate-400">
                        Executions: {workflow.executionCount || 0}
                      </span>
                    </div>
                    {workflow.trigger && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="material-symbols-outlined text-slate-500 text-sm">sensors</span>
                        <span className="text-slate-400">
                          Trigger: {workflow.trigger}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleExecuteWorkflow(workflow.id)}
                      className="flex-1 bg-[#13a4ec] hover:bg-[#13a4ec]/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <span className="material-symbols-outlined text-sm">play_arrow</span>
                      Execute
                    </button>
                    <button
                      onClick={() => navigate(`/workflows/${workflow.id}`)}
                      className="px-4 py-2 border border-[#233c48] text-slate-400 hover:text-white hover:border-[#13a4ec]/50 rounded-lg text-sm font-medium transition-colors"
                    >
                      <span className="material-symbols-outlined text-sm">edit</span>
                    </button>
                    <button
                      onClick={() => handleDeleteWorkflow(workflow.id)}
                      className="px-4 py-2 border border-red-500/20 text-red-400 hover:text-red-300 hover:border-red-500/50 rounded-lg text-sm font-medium transition-colors"
                    >
                      <span className="material-symbols-outlined text-sm">delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick Stats */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-[#16252d] border border-[#233c48] rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#13a4ec]/10 rounded-lg flex items-center justify-center">
                <span className="material-symbols-outlined text-[#13a4ec] text-xl">workflow</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{workflows.length}</p>
                <p className="text-slate-400 text-sm">Total Workflows</p>
              </div>
            </div>
          </div>

          <div className="bg-[#16252d] border border-[#233c48] rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                <span className="material-symbols-outlined text-green-400 text-xl">check_circle</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{workflows.filter(w => w.status === 'ACTIVE').length}</p>
                <p className="text-slate-400 text-sm">Active</p>
              </div>
            </div>
          </div>

          <div className="bg-[#16252d] border border-[#233c48] rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center">
                <span className="material-symbols-outlined text-yellow-400 text-xl">pause_circle</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{workflows.filter(w => w.status === 'PAUSED').length}</p>
                <p className="text-slate-400 text-sm">Paused</p>
              </div>
            </div>
          </div>

          <div className="bg-[#16252d] border border-[#233c48] rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
                <span className="material-symbols-outlined text-red-400 text-xl">error</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{workflows.filter(w => w.status === 'FAILED').length}</p>
                <p className="text-slate-400 text-sm">Failed</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Workflow Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#16252d] border border-[#233c48] rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Create New Workflow</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Workflow Name</label>
                <input
                  type="text"
                  placeholder="Enter workflow name"
                  className="w-full px-3 py-2 bg-[#0a1116] border border-[#233c48] rounded-lg text-white placeholder:text-slate-500 focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Description</label>
                <textarea
                  placeholder="Describe what this workflow does"
                  rows={3}
                  className="w-full px-3 py-2 bg-[#0a1116] border border-[#233c48] rounded-lg text-white placeholder:text-slate-500 focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Trigger Type</label>
                <select className="w-full px-3 py-2 bg-[#0a1116] border border-[#233c48] rounded-lg text-white focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent">
                  <option value="manual">Manual</option>
                  <option value="schedule">Scheduled</option>
                  <option value="webhook">Webhook</option>
                  <option value="event">Event-based</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 border border-[#233c48] text-slate-400 hover:text-white hover:border-[#13a4ec]/50 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  navigate('/workflow-builder');
                }}
                className="flex-1 bg-[#13a4ec] hover:bg-[#13a4ec]/90 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Create & Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowsPage;